import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { FC, memo, useMemo } from 'react'
import { CheckRounded, Favorite, Note as NoteIcon, Visibility } from '@mui/icons-material'
import { treatString } from '../../../../../utils'
import { ProductModel } from "../../../../../models/Product.model"
import { HexTile } from "../../../../../components/ui/Hexagon"
import TagChip from "../../../../../components/ui/TagChip"
import { useRowToggle } from "../../../../../components/ui"

interface ActiveItemComponentProps {
    checked?: boolean,
    props: ProductModel,
    onClick: (item: ProductModel) => void
    onSelect: (item: ProductModel, checked: boolean) => void,
    onFavorite: (patient: ProductModel) => void,
    annotationOpen?: (patient: ProductModel) => void,
    onPopUp: () => void
}

const ActiveItem: FC<ActiveItemComponentProps> = ({ props, onSelect, checked, onClick, onPopUp, onFavorite, annotationOpen }) => {
    const { productName, classes } = props
    const theme = useTheme()
    const toggleProps = useRowToggle(() => onSelect(props, !checked), { checked: !!checked, label: `Selecionar ${productName}` })
    const displayName = useMemo(() => treatString(productName.toLowerCase(), 40, { capitalize: true }), [productName])

    return (
        <Box
            {...toggleProps}
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                borderRadius: '14px',
                border: `1.5px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: checked ? theme.palette.brand.cream : 'background.paper',
                p: '16px',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': { borderColor: checked ? theme.palette.primary.main : theme.palette.brand.creamStroke },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
        >
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <HexTile label={productName} size={40} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                        {displayName}
                    </Typography>
                    {classes && (
                        <Box sx={{ mt: '5px' }}>
                            <TagChip label={treatString(classes, 28)} />
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                        backgroundColor: checked ? theme.palette.primary.main : 'transparent',
                        transition: 'background-color 0.15s, border-color 0.15s',
                    }}
                >
                    {checked && <CheckRounded sx={{ fontSize: 15, color: 'primary.contrastText' }} />}
                </Box>
            </Box>

            {/* Só os ícones ficam fora do toggle do card: a caixa encolhe ao conteúdo
                (alignSelf) em vez de ocupar a linha inteira, senão o espaço vazio à
                esquerda dos ícones engolia o clique sem selecionar nada. */}
            <Box
                data-testid="active-item-actions"
                sx={{ display: 'flex', alignItems: 'center', gap: '2px', alignSelf: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
            >
                <Tooltip title="Informações" placement="bottom">
                    <IconButton size="small" aria-label={`Informações de ${productName}`} onClick={() => onClick(props)} sx={{ color: 'primary.main' }}>
                        <Visibility sx={{ fontSize: 19 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Favoritar" placement="bottom">
                    <IconButton size="small" aria-label={`Favoritar ${productName}`} onClick={() => onFavorite(props)}>
                        <Favorite color={props.favorite ? 'primary' : 'disabled'} sx={{ fontSize: 19 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Anotações" placement="bottom">
                    <IconButton size="small" aria-label={`Anotações de ${productName}`} onClick={() => (annotationOpen ? annotationOpen(props) : onPopUp())} sx={{ color: annotationOpen ? 'primary.main' : 'text.disabled' }}>
                        <NoteIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    )
}

// memo: a lista tem centenas de cards e cada um monta 3 Tooltips; sem isso,
// selecionar um ativo re-renderizava todos e a etapa ficava lenta.
export default memo(ActiveItem)
